import type { PerformanceTimingData } from 'src/common/types';

const PERFORMANCE_THRESHOLDS = {
  MIN_LOAD_TIME: 0,
  MAX_LOAD_TIME: 120000, // 2 minutes to accommodate slow networks
};

// Track which navigation session we've already captured performance data for
const navigationSessionTracker = { capturedNavigationStart: null as number | null };

/**
 * Measures performance synchronously without blocking - returns immediately
 * Perfect for ensuring page view events are never delayed
 * Returns performance data for initial page loads, null for SPA navigation
 */
export const measurePerformanceSync = (): PerformanceTimingData | null => {
  try {
    if (!('performance' in window)) return null;

    // Check if this is likely an initial page load or SPA navigation
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const navEntry = navEntries[0];

    if (!navEntry) {
      return null;
    }

    // If load event hasn't completed yet, use domContentLoaded as fallback
    const endTime = navEntry.loadEventEnd || navEntry.domContentLoadedEventEnd;
    if (!endTime) {
      return null;
    }

    // Check navigation type - only track real navigations, not SPA route changes
    // Navigation types: navigate, reload, back_forward, prerender
    if (navEntry.type !== 'navigate' && navEntry.type !== 'reload') {
      return null;
    }

    // For SPA navigation detection: if we've already captured performance data
    // for this navigation session, return null (this is SPA navigation)
    if (navigationSessionTracker.capturedNavigationStart === navEntry.fetchStart) {
      return null;
    }

    const totalLoadTime = endTime - navEntry.fetchStart;

    if (totalLoadTime < PERFORMANCE_THRESHOLDS.MIN_LOAD_TIME || totalLoadTime > PERFORMANCE_THRESHOLDS.MAX_LOAD_TIME) {
      return null;
    }

    // Get FCP if available
    const paintEntries = performance.getEntriesByType('paint') as PerformanceEntry[];
    const fcpEntry = paintEntries.find((entry) => entry.name === 'first-contentful-paint');

    // Mark this navigation session as captured to prevent duplicate data on SPA navigation
    navigationSessionTracker.capturedNavigationStart = navEntry.fetchStart;

    return {
      totalLoadTime,
      firstContentfulPaint: fcpEntry?.startTime,
      largestContentfulPaint: undefined, // LCP requires async measurement
    };
  } catch {
    return null;
  }
};
